import { ApolloError, gql, useLazyQuery } from '@apollo/client';
import { encodeToBase64 } from '@modules/common/utils';
import { uploadFileToURL } from '@modules/integrationsV2/utils';
import { FileType } from 'generated/graphql';
import { useEffect, useRef, useState } from 'react';
import { useGetUser } from 'v2/core/services/user';

const GET_UPLOAD_URL_QUERY = gql`
  query getFileUploadUrl(
    $tenantID: ID!
    $userID: ID!
    $integrationID: ID!
    $fileName: String!
    $fileType: FileType!
  ) {
    getFileUploadUrl(
      request: {
        tenantID: $tenantID
        userID: $userID
        integrationID: $integrationID
        fileName: $fileName
        fileType: $fileType
      }
    ) {
      url
      s3Key
    }
  }
`;

interface UploadCSVFileOptions {
  file: File;
  integrationID: string;
  onError: (error: ApolloError | undefined) => void;
}

export const useUploadCSVFile = () => {
  const {
    tenant: { id: tenantID },
    id: userID,
  } = useGetUser();

  const [getFileUploadUrl] = useLazyQuery<{
    getFileUploadUrl: { url: string; s3Key: string };
  }>(GET_UPLOAD_URL_QUERY, {
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
  });

  const [fileURL, setFileURL] = useState<string>();
  const [s3Key, setS3Key] = useState<string>();
  const [isCancelled, setIsCancelled] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const xhrRequest = useRef<XMLHttpRequest>(new XMLHttpRequest());

  const cancelFileUpload = () => {
    setIsCancelled(true);
  };

  useEffect(() => {
    if (isCancelled && xhrRequest.current.readyState !== 0) {
      xhrRequest.current.abort();

      setIsUploading(false);
      setIsCancelled(false);
    }
  }, [isCancelled]);

  const uploadCSVFile = async ({
    file,
    integrationID,
    onError,
  }: UploadCSVFileOptions) => {
    // Reset states
    setIsCompleted(false);
    setIsUploading(true);
    setFileURL(undefined);
    setS3Key(undefined);

    // Get the pre-signed upload URL from the server.
    const { data: fileUploadUrlData, error: fileUploadUrlError } =
      await getFileUploadUrl({
        variables: {
          tenantID,
          userID,
          integrationID,
          fileName: encodeToBase64(file.name),
          fileType: FileType.Csv,
        },
      });

    // If there is an error, no need to continue.
    if (!fileUploadUrlData || fileUploadUrlError) {
      cancelFileUpload();
      onError(fileUploadUrlError);
      return;
    }

    setFileURL(fileUploadUrlData.getFileUploadUrl.url);
    setS3Key(fileUploadUrlData.getFileUploadUrl.s3Key);

    // Upload the file to the pre-signed URL.
    await uploadFileToURL({
      file,
      userID,
      setUploadProgress: setProgress,
      xhr: xhrRequest.current,
      url: fileUploadUrlData.getFileUploadUrl.url,
    });

    // Once file is uploaded, set uploading states to false and mark as completed
    setIsCompleted(true);
    setIsUploading(false);
  };

  return [
    uploadCSVFile,
    {
      fileURL,
      s3Key,
      progress,
      isUploading,
      isCompleted,
      cancelFileUpload,
    },
  ] as const;
};
